import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { AppLayout } from "@/components/intel/Layout";
import { fetchThreatFeed, type Severity } from "@/lib/intel/feeds.functions";
import { SeverityBadge } from "@/components/intel/SeverityBadge";
import { ShieldAlert, AlertTriangle, Filter, RefreshCw, ExternalLink } from "lucide-react";

export const Route = createFileRoute("/threats")({
  head: () => ({ meta: [{ title: "Threat Center · CARIO INTEL" }] }),
  component: Threats,
});

function Threats() {
  const fetcher = useServerFn(fetchThreatFeed);
  const { data, isLoading, isFetching, refetch, error } = useQuery({
    queryKey: ["threats"],
    queryFn: () => fetcher(),
    staleTime: 5 * 60_000,
  });
  const [sev, setSev] = useState<Severity | "all">("all");
  const [q, setQ] = useState("");
  const items = data ?? [];
  const list = items.filter((f) =>
    (sev === "all" || f.severity === sev) &&
    (q.trim() === "" || (f.title + f.description + f.source + f.cve + f.vendor).toLowerCase().includes(q.toLowerCase()))
  );
  const counts = (s: Severity) => items.filter((f) => f.severity === s).length;
  return (
    <AppLayout>
      <div className="p-3 space-y-3">
        <div className="glass rounded-xl p-4">
          <div className="flex items-center gap-3">
            <ShieldAlert className="h-5 w-5 text-amber" />
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-cyan">Threat center · CISA KEV + NVD live feed</div>
              <h1 className="font-display text-2xl">Active vulnerabilities & exploitation indicators</h1>
            </div>
            <button onClick={() => refetch()} className="ml-auto inline-flex items-center gap-2 rounded-md border border-panel-border bg-panel/40 px-3 py-1.5 text-xs"><RefreshCw className={`h-3 w-3 ${isFetching ? "animate-spin" : ""}`} />Refresh</button>
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {(["critical","high","medium","low"] as Severity[]).map((s) => (
              <button key={s} onClick={() => setSev(s === sev ? "all" : s)}
                className={`rounded-md border p-3 text-left transition-colors ${sev === s ? "border-cyan/50 bg-cyan/10" : "border-panel-border bg-background/40 hover:bg-panel/60"}`}>
                <div className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{s}</div>
                <div className="mt-1 font-display text-2xl">{counts(s)}</div>
              </button>
            ))}
          </div>
        </div>
        <div className="glass rounded-xl p-3">
          <div className="flex items-center gap-2 mb-3">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-panel-border bg-background/40 px-2 py-1.5">
              <Filter className="h-3.5 w-3.5 text-muted-foreground" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter by CVE, vendor, keyword…" className="flex-1 bg-transparent text-xs outline-none" />
            </div>
            <button onClick={() => { setSev("all"); setQ(""); }} className="rounded-md border border-panel-border bg-panel/40 px-2 py-1.5 text-xs">Reset</button>
          </div>
          <div className="space-y-2">
            {isLoading && <div className="rounded-md border border-dashed border-panel-border p-8 text-center text-sm text-muted-foreground">Pulling live feed from CISA KEV + NVD…</div>}
            {error && <div className="rounded-md border border-danger/40 bg-danger/10 p-4 text-sm text-danger">{(error as Error).message}</div>}
            {!isLoading && list.length === 0 && <div className="rounded-md border border-dashed border-panel-border p-8 text-center text-sm text-muted-foreground">No matching findings.</div>}
            {list.map((f) => (
              <a key={f.id} href={f.ref} target="_blank" rel="noopener noreferrer" className="block rounded-md border border-panel-border bg-background/40 p-3 hover:bg-panel/60">
                <div className="flex items-start gap-3">
                  <div className="grid h-9 w-9 shrink-0 place-items-center rounded-md bg-amber/10 ring-1 ring-amber/30"><AlertTriangle className="h-4 w-4 text-amber" /></div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <SeverityBadge s={f.severity} />
                      <span className="font-mono text-[10px] uppercase tracking-widest text-cyan">{f.cve}</span>
                      {f.cvss != null && <span className="font-mono text-[10px] text-amber">CVSS {f.cvss.toFixed(1)}</span>}
                      <span className="font-mono text-[10px] text-muted-foreground">· {f.publishedAt.slice(0, 10)}</span>
                      <span className="ml-auto inline-flex items-center gap-1 font-mono text-[10px] text-cyan">{f.source}<ExternalLink className="h-3 w-3" /></span>
                    </div>
                    <div className="mt-1 text-sm font-medium">{f.title}</div>
                    <div className="mt-0.5 text-sm text-muted-foreground">{f.description}</div>
                    {(f.vendor || f.product) && <div className="mt-1.5 font-mono text-[10px] uppercase tracking-widest text-muted-foreground">{[f.vendor, f.product].filter(Boolean).join(" / ")}</div>}
                  </div>
                </div>
              </a>
            ))}
          </div>
        </div>
      </div>
    </AppLayout>
  );
}